import { type ReactNode } from 'react'
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { AuthProvider, useAuth } from './hooks/useAuth'
import OfficeBrand, { officeProductDescription } from './OfficeBrand'
import OfficeWorkspace from './OfficeWorkspace'
import TeamAdministration from './TeamAdministration'
import JoinTeam from './JoinTeam'
import ConnectedFolders from './ConnectedFolders'
import ProjectWorkspace from './ProjectWorkspace'
import NativeEditor from './NativeEditor'
import {SuiteHome,SuiteAppPage} from './Suite'
import WorkspaceLogin from './WorkspaceLogin'
import PdfWorkspace from './PdfWorkspace'

function ProtectedRoute({ children }: { children: ReactNode }) {
  const location = useLocation()
  const { authenticated, loading } = useAuth()
  if (loading) return <p className="folio-loading" role="status">Loading Folio…</p>
  if (!authenticated) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
  return <>{children}</>
}

function AuthPage() {
  return <div className="product-site folio-auth"><div className="product-container"><header className="product-nav"><Link to="/" aria-label="Folio home"><OfficeBrand /></Link><Link to="/login">Sign in</Link></header><main className="product-hero"><div><span className="product-kicker">Folio</span><h1>Join your<br /><em>team’s workspace.</em></h1><p className="product-lead">Open the invitation link from your team manager, then sign in with your verified organization account.</p><p>A platform administrator establishes shared accounts and email verification. Your workspace administrator assigns workspace access.</p><p className="folio-endorsement">Based on Contextful &amp; Forge</p></div><div className="product-auth"><OfficeBrand /><h2>Have an invitation?</h2><p>Use its link to review and accept team access. A normal sign-in does not add you to a team.</p><Link className="product-button" to="/login">Sign in to an existing workspace<ArrowRight size={16}/></Link></div></main></div></div>
}

function FolioHome(){const location=useLocation();return new URLSearchParams(location.search).has("file")?<OfficeWorkspace/>:<SuiteHome/>}

export default function App() {
  return <AuthProvider><Routes>
    <Route path="/" element={<ProtectedRoute><FolioHome /></ProtectedRoute>} />
    <Route path="/files" element={<ProtectedRoute><OfficeWorkspace /></ProtectedRoute>} />
    <Route path="/apps/pdf" element={<ProtectedRoute><PdfWorkspace /></ProtectedRoute>} />
    <Route path="/apps/:appId" element={<ProtectedRoute><SuiteAppPage /></ProtectedRoute>} />
    <Route path="/projects" element={<ProtectedRoute><ProjectWorkspace /></ProtectedRoute>} />
    <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectWorkspace /></ProtectedRoute>} />
    <Route path="/teams" element={<ProtectedRoute><TeamAdministration /></ProtectedRoute>} />
    <Route path="/connections" element={<ProtectedRoute><ConnectedFolders /></ProtectedRoute>} />
    <Route path="/native/:fileId" element={<ProtectedRoute><NativeEditor /></ProtectedRoute>} />
    <Route path="/join" element={<JoinTeam />} />
    <Route path="/login" element={<WorkspaceLogin />} />
    <Route path="/t/:tenant/login" element={<WorkspaceLogin />} />
    <Route path="/signup" element={<AuthPage />} />
    <Route path="/office" element={<Navigate to="/" replace />} />
    <Route path="/office/login" element={<Navigate to="/login" replace />} />
    <Route path="/office/signup" element={<Navigate to="/signup" replace />} />
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes></AuthProvider>
}
